import type { Backlink } from '@api/types/backlinks';
import type { UserLite } from '@api/types/user';
import { DocumentRelatedLinks } from '@components/common/RelatedLinksIndicator';
import EditableDocumentName from '@components/EditableDocumentName';
import {
  FileExtension,
  INLINE_EDIT_PENCIL_CLASS,
  InlineEditField,
  type FileExtensionProps,
  type InlineEditFieldProps,
} from '@shared/ui';
import toFullName from '@constants/toFullName';
import { Box, IconButton, LinearProgress, Stack } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import { getHumanFileSize } from '@utils/files';
import { MoreVertical, X } from 'lucide-react';
import React from 'react';

export type DocumentFileRowStatusTone = 'muted' | 'success' | 'error';

export interface DocumentFileRowProps {
  name: string;
  /**
   * The file's URL or name — the source of the file-type icon only. It opens nothing:
   * a row opens through `href` or `onOpen`, so an upload with no saved document yet
   * reads as plain, not clickable.
   */
  documentUrl?: string | null;
  /**
   * Makes the whole row a real link that opens `href` in a new tab, so middle-click,
   * Cmd-click and "Copy link address" work natively. Ignored when `onOpen` is set.
   */
  href?: string | null;
  disableLink?: boolean;
  /**
   * When provided, the whole row becomes clickable and calls this on click /
   * Enter / Space (e.g. to open the in-app document view page). Takes precedence
   * over `href`.
   */
  onOpen?: () => void;
  iconSize?: FileExtensionProps['size'];
  sizeBytes?: number | null;
  backlinks?: Backlink[];
  addedBy?: UserLite;
  statusLabel?: string | null;
  statusTone?: DocumentFileRowStatusTone;
  trailingLabel?: string | null;
  progress?: number | null;
  statusTagLabel?: string | null;
  disabled?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
  actionDataTestId?: string;
  showMoreAction?: boolean;
  /**
   * Trailing actions node (e.g. a kebab "…" menu) rendered before the optional
   * remove (X) button. Lets feature layers inject business actions without the
   * shared row taking on any document logic.
   */
  moreActions?: React.ReactNode;
  /**
   * A node drawn right after the name, on the title line: a feature's own tag about this
   * file (the import wizard's *Imported* / *Superseded*). Raised above the row's link so a
   * tooltip on it can be hovered. Hidden while the name is being edited.
   */
  titleAdornment?: React.ReactNode;
  /**
   * When true (and `documentId` is a real saved id), the title becomes an
   * inline-editable name with a pencil affordance on hover, reusing
   * {@link EditableDocumentName}. Editability is further gated server-side and
   * by that component (read-only role / archived audit fall back to plain text).
   */
  editableName?: boolean;
  documentId?: number;
  onRenameSaved?: (name: string) => void;
  /**
   * Renames inline through the caller's own save instead of {@link EditableDocumentName}'s
   * audit-document rename: for a row whose file is no audit document (an example document).
   * The caller decides who may rename, so the pencil shows whenever this is set, and
   * `editableName` / `documentId` are not consulted.
   */
  onRenameCommit?: InlineEditFieldProps['onCommit'];
  /** The rename field's `maxLength`, for an `onRenameCommit` save the server bounds. */
  renameMaxLength?: number;
  'data-testid'?: string;
}

const Root = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})<{ clickable?: boolean }>(({ theme, clickable }) => ({
  position: 'relative',
  // Its own stacking context: the row link and the controls lifted above it stack
  // inside the row, never over the page's sticky bars.
  isolation: 'isolate',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  minHeight: 58,
  width: '100%',
  padding: theme.spacing(1),
  border: '1px solid #E5ECED',
  borderRadius: 8,
  backgroundColor: 'transparent',
  color: 'inherit',
  textDecoration: 'none',
  overflow: 'hidden',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'background-color 180ms ease, border-color 180ms ease, opacity 180ms ease',
  '&:hover': clickable
    ? {
        backgroundColor: '#F8F8FA',
        borderColor: '#CCD8DA',
        '& .DocumentFileRow-title': {
          textDecoration: 'underline',
        },
        // The row link covers the name, so the name's own hover can't reveal the pencil.
        [`& .${INLINE_EDIT_PENCIL_CLASS}`]: { opacity: 1 },
      }
    : undefined,
  '&:focus-visible': clickable
    ? {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      }
    : undefined,
}));

/**
 * A link row's link: laid over the whole row as a SIBLING of its controls, not wrapped
 * round them — interactive content inside an `<a>` is invalid HTML, and a screen reader
 * or the keyboard then reads the row as one link. A click anywhere off a control lands
 * on it; the controls sit above it (`raised`).
 */
const RowLink = styled('a')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  borderRadius: 'inherit',
  // Inset: the row's `overflow: hidden` would clip a ring drawn outside the link.
  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
}));

/** Lifts a control above the row link, so it takes its own pointer events. */
const raised = { position: 'relative', zIndex: 2 } as const;

const TitleText = styled('span')(({ theme }) => ({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.6,
  color: '#2C3A42',
}));

// `EditableDocumentName` applies the passed className to its inner name node;
// align it with `TitleText` so the editable variant matches the static one.
const editableTitleStyles = ({ theme }: { theme: Theme }) => ({
  minWidth: 0,
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.6,
  color: '#2C3A42',
  // The name stays under the row link (a click on it opens the document); the pencil
  // is already positioned, so it only needs lifting above the link.
  [`& .${INLINE_EDIT_PENCIL_CLASS}`]: { zIndex: raised.zIndex },
});

const EditableTitle = styled(EditableDocumentName)(editableTitleStyles);

/** The same title for a caller-saved rename (`onRenameCommit`). */
const CallerRenamedTitle = styled(InlineEditField)(editableTitleStyles);

const MetaText = styled('span')<{ $tone?: DocumentFileRowStatusTone }>(({ theme, $tone }) => ({
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.36,
  color:
    $tone === 'success'
      ? theme.palette.success.main
      : $tone === 'error'
        ? theme.palette.error.main
        : '#809EA3',
}));

const Separator = styled('span')(() => ({
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.36,
  color: '#809EA3',
}));

const StatusTag = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minHeight: 20,
  padding: theme.spacing(0.25, 1, 0.25, 0.25),
  borderRadius: 80,
  backgroundColor: '#F2F5F6',
  color: '#00343C',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.36,
  whiteSpace: 'nowrap',
}));

const StatusDot = styled('span')(() => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: '1.5px solid #CCD8DA',
  backgroundColor: 'transparent',
  flexShrink: 0,
}));

/** Sized and inked like the row's ⋮ (`DocumentActionsMenu`); both take the theme's hover. */
const ActionIconButton = styled(IconButton)({
  width: 28,
  height: 28,
  marginTop: -4,
  marginRight: -6,
  color: '#809EA3',
});

export const DocumentFileRow: React.FC<DocumentFileRowProps> = ({
  name,
  documentUrl,
  href,
  disableLink = false,
  iconSize = 'xlarge',
  sizeBytes,
  backlinks = [],
  addedBy,
  statusLabel,
  statusTone = 'muted',
  trailingLabel,
  progress,
  statusTagLabel,
  disabled = false,
  onOpen,
  onRemove,
  removeLabel = 'Remove document',
  actionDataTestId,
  showMoreAction = false,
  moreActions,
  titleAdornment,
  editableName = false,
  documentId,
  onRenameSaved,
  onRenameCommit,
  renameMaxLength,
  'data-testid': dataTestId,
}) => {
  const canEditName = editableName && documentId != null && documentId > 0;
  const [isEditingName, setIsEditingName] = React.useState(false);
  const sizeLabel = sizeBytes != null && sizeBytes > 0 ? getHumanFileSize(sizeBytes) : null;
  // A link row drops its link while disabled or renaming, so a click in the rename
  // field opens nothing. The link is a sibling of the title, so the title stays mounted.
  const isLink = !onOpen && !disableLink && Boolean(href);
  const clickable = !disabled && (Boolean(onOpen) || isLink);
  const linkHref = isLink && !disabled && !isEditingName ? (href ?? undefined) : undefined;

  const handleActivate = () => {
    if (!clickable) return;
    onOpen?.();
  };

  const hasBacklinks = backlinks.length > 0;
  const uploaderName = addedBy ? toFullName(addedBy).trim() : '';
  const metaItems = [
    hasBacklinks ? (
      // Above the row link: its hover opens the backlinks list.
      <Box key='backlinks' sx={{ ...raised, display: 'flex' }}>
        <DocumentRelatedLinks backlinks={backlinks} />
      </Box>
    ) : null,
    sizeLabel ? <MetaText key='size'>{sizeLabel}</MetaText> : null,
    uploaderName ? <MetaText key='addedBy'>Uploaded by {uploaderName}</MetaText> : null,
    statusLabel ? (
      <MetaText key='status' $tone={statusTone}>
        {statusLabel}
      </MetaText>
    ) : null,
  ].filter(Boolean);

  return (
    <Root
      data-testid={dataTestId}
      clickable={clickable}
      sx={{ opacity: disabled ? 0.64 : 1 }}
      {...(isLink
        ? {}
        : {
            'aria-label': clickable ? name : undefined,
            onClick: clickable ? handleActivate : undefined,
            role: clickable ? 'button' : undefined,
            tabIndex: clickable ? 0 : undefined,
            onKeyDown: clickable
              ? (event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleActivate();
                  }
                }
              : undefined,
          })}
    >
      {linkHref ? (
        // `title`: the link covers the name, so it carries the full (truncated) name's hover.
        <RowLink
          href={linkHref}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={name}
          title={name}
        />
      ) : null}

      <Stack direction='row' alignItems='flex-start' gap={1} width='100%'>
        <FileExtension size={iconSize} document={documentUrl || name} />

        <Stack flex={1} minWidth={0} spacing={0.25} alignItems='stretch'>
          <Stack direction='row' alignItems='center' gap={1} minWidth={0}>
            {onRenameCommit ? (
              <CallerRenamedTitle
                value={name}
                size='medium'
                maxLength={renameMaxLength}
                onCommit={onRenameCommit}
                onEditingChange={setIsEditingName}
                testIdPrefix='editable-document-name'
              />
            ) : canEditName ? (
              <EditableTitle
                id={documentId}
                name={name}
                size='medium'
                onSave={onRenameSaved}
                onEditingChange={setIsEditingName}
              />
            ) : (
              <TitleText className='DocumentFileRow-title'>{name}</TitleText>
            )}

            {titleAdornment && !isEditingName ? (
              <Box sx={{ ...raised, display: 'flex', flexShrink: 0 }}>{titleAdornment}</Box>
            ) : null}

            {statusTagLabel ? (
              <Box sx={{ ml: 'auto', minWidth: 0, flexShrink: 0 }}>
                <StatusTag>
                  <StatusDot />
                  {statusTagLabel}
                </StatusTag>
              </Box>
            ) : null}
          </Stack>

          {metaItems.length > 0 || trailingLabel ? (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              gap={1}
              // Keep the row height stable while renaming — hide, don't unmount.
              sx={{ visibility: isEditingName ? 'hidden' : 'visible' }}
            >
              <Stack direction='row' spacing={0.5} alignItems='center' flexWrap='wrap' useFlexGap>
                {metaItems.map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 ? <Separator>|</Separator> : null}
                    {item}
                  </React.Fragment>
                ))}
              </Stack>

              {trailingLabel ? <MetaText>{trailingLabel}</MetaText> : null}
            </Stack>
          ) : null}
        </Stack>

        {moreActions || onRemove || showMoreAction ? (
          <Stack
            direction='row'
            alignItems='flex-start'
            spacing={0.25}
            // Above the row link, and outside it: a click or middle-click here never opens it.
            sx={{ ...raised, flexShrink: 0 }}
            // Trailing actions are interactive controls, not row activation.
            onClick={(event) => event.stopPropagation()}
          >
            {moreActions}
            {onRemove ? (
              <ActionIconButton
                size='small'
                aria-label={removeLabel}
                data-testid={actionDataTestId}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove();
                }}
              >
                <X size={16} />
              </ActionIconButton>
            ) : showMoreAction && !moreActions ? (
              <ActionIconButton
                size='small'
                aria-label='Document actions'
                disabled={disabled}
                onClick={(event) => event.stopPropagation()}
              >
                <MoreVertical size={16} />
              </ActionIconButton>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      {progress != null ? (
        <Box
          sx={{
            width: '100%',
            pt: 0.25,
            '& .MuiLinearProgress-root': {
              height: 4,
              borderRadius: 999,
              backgroundColor: 'grey.100',
            },
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              background: (theme) =>
                `linear-gradient(90deg, ${theme.palette.success.light} 0%, ${theme.palette.success.light} 50%, ${theme.palette.success.main} 100%)`,
            },
          }}
        >
          <LinearProgress variant='determinate' value={Math.min(100, Math.max(0, progress))} />
        </Box>
      ) : null}
    </Root>
  );
};

export default DocumentFileRow;
